/**
 * "Field Diary" — the original @dala.explorer carousel design.
 * Dark gradient overlays, elegant serif headline, 4-segment stat row.
 * Extracted from lib/carousel-canvas.ts; drawing logic unchanged except
 * the generalized data fields (kickerText, stats[], namePrimary/…).
 */

import { SLIDE_W, SLIDE_H, type CarouselTemplate, type CoverSlideData, type SpeciesSlideData, type ClosingSlideData, type SlideStyle, type DrawOpts } from "../types";
import {
  drawCoverFitImage, drawWatermark, FLAT_BG_COLORS,
  drawEdgeScrims, drawCurvedArrow, drawTrackedText, measureTracked,
  fitSerifHeadline,
} from "../shared";
import { SCRIPT_FONT } from "../fonts";

/** Scratch canvas used only to measure text (never drawn to) — lets
 * coverTextLayout() replicate drawCover's wrapTopDown() line count without
 * needing the live stage canvas's own context. Client-only, same as every
 * other canvas usage in this file. */
let measureCanvas: HTMLCanvasElement | null = null;
function getMeasureCtx(): CanvasRenderingContext2D {
  if (!measureCanvas) measureCanvas = document.createElement("canvas");
  return measureCanvas.getContext("2d")!;
}

/* ── Cover geometry, in logical 1080×1350 slide space ──────────────────
   Traced from the @dala.explorer reference cover: transparent background
   (the photo shows through), two 450px edge scrims, a tracked-caps kicker,
   a shrink-to-fit serif place name, a tracked-caps region·month line, and a
   bottom row of icon-over-value stats with a script swipe cue opposite. */
const COVER_PAD = 85;
const COVER_SCRIM_H = 450;
const COVER_KICKER_MID = 100;
/** Cap top of the headline block — it grows downward as lines are added. */
const COVER_HEADLINE_TOP = 165;
/** Headline block bottom → subtitle middle. */
const COVER_SUBTITLE_GAP = 52;
const COVER_STAT_VALUE_MID = SLIDE_H - 155;
const COVER_STAT_ICON_MID = SLIDE_H - 232;
const COVER_STAT_GAP = 58;

/** One single-line text box — `middle` is the vertical center, so the canvas
 * (textBaseline "middle") and the live editor's HTML overlay (an <input>
 * centered on the same y) land on the same pixels. Single source of truth
 * shared by drawCover below and FieldDiaryCoverOverlay.tsx — never
 * hand-duplicate these numbers, they have to agree with the export. */
export interface CoverTextBox {
  left: number; middle: number; width: number;
  fontSize: number; fontWeight: number; family: "display" | "body"; color: string;
  /** Extra px between glyphs (canvas draws it manually; CSS letter-spacing). */
  tracking: number;
  uppercase: boolean;
}

export function coverTextLayout(data: CoverSlideData, fonts: { display: string; body: string }): {
  kicker: CoverTextBox;
  headline: CoverTextBox & { lineHeight: number; lineCount: number };
  subtitle: CoverTextBox;
} {
  const W = SLIDE_W;
  const maxW = W - COVER_PAD * 2;
  // This template's text is white by default (it's designed for a dark
  // background). The only curated flat color that breaks that contrast is
  // "light" (white-on-white) — flip text to dark ink in that one case.
  const textOnLight = !data.photo && data.backgroundMode === "light";
  const rgb = textOnLight ? "28,36,32" : "255,255,255";

  const kicker: CoverTextBox = {
    left: COVER_PAD, middle: COVER_KICKER_MID, width: maxW,
    fontSize: 30 * (data.kickerScale ?? 1), fontWeight: 600, family: "body",
    color: `rgba(${rgb},0.72)`, tracking: 10 * (data.kickerScale ?? 1), uppercase: true,
  };

  // Shrink-to-fit the place name, then hand the resulting metrics to both the
  // canvas and the overlay so a 1- vs 2-line title moves the subtitle in lockstep.
  const fit = fitSerifHeadline(
    getMeasureCtx(), data.headlineTitle, 0, maxW, data.headlineFontSize, fonts.display,
    { weight: 300, capTop: COVER_HEADLINE_TOP }
  );
  const lineCount = Math.max(1, fit.lines.length);
  const headline: CoverTextBox & { lineHeight: number; lineCount: number } = {
    left: COVER_PAD, middle: COVER_HEADLINE_TOP + (lineCount * fit.lineH) / 2, width: maxW,
    fontSize: fit.usedSize, fontWeight: 300, family: "display",
    color: textOnLight ? "#1c2420" : "#fff", tracking: 0, uppercase: false,
    lineHeight: fit.lineH, lineCount,
  };

  const subtitle: CoverTextBox = {
    left: COVER_PAD,
    middle: COVER_HEADLINE_TOP + lineCount * fit.lineH + COVER_SUBTITLE_GAP,
    width: maxW,
    fontSize: 30 * (data.subtitleScale ?? 1), fontWeight: 500, family: "body",
    color: `rgba(${rgb},0.75)`, tracking: 8 * (data.subtitleScale ?? 1), uppercase: true,
  };

  return { kicker, headline, subtitle };
}

/** One name line's box, in logical 1080×1350 slide space — `bottom` is the
 * distance from the slide's bottom edge (CSS-style), matching how the live
 * editor's HTML text overlay positions its <input> for this same line.
 * Single source of truth for both the canvas draw below and that overlay
 * (components/carousel/FieldDiarySpeciesOverlay.tsx) — never hand-duplicate
 * these numbers, they have to agree pixel-for-pixel with the export. */
export interface SpeciesNameBox {
  left: number; bottom: number; height: number;
  fontSize: number; fontWeight: number; family: "display" | "body";
  color: string;
}

/**
 * All three lines are optional now — which name occupies which slot (and
 * therefore which size) depends on the active name-language mode (see
 * assignNameSlots() in lib/carousel/names.ts), not a fixed language. Lines
 * stack bottom-up starting from whichever is lowest actually present, so
 * e.g. English-only (just `primary` populated) sits flush on the same
 * baseline a Kazakh-primary/English-tertiary card would use for its bottom
 * line — no reserved blank space.
 */
export function speciesNameLayout(data: SpeciesSlideData): {
  tertiary: SpeciesNameBox | null;
  secondary: SpeciesNameBox | null;
  primary: SpeciesNameBox | null;
} {
  const H = SLIDE_H;
  const LEFT = 60;
  const containerBottom = H - 60;
  const TER_H = 84, SEC_H = 84, PRI_H = 122;
  const GAP_SEC_TER = 6, GAP_PRI_SEC = 19;

  let cursorBottom = containerBottom;

  let tertiary: SpeciesNameBox | null = null;
  if (data.nameTertiary) {
    tertiary = {
      left: LEFT, bottom: H - cursorBottom, height: TER_H,
      fontSize: 38, fontWeight: 400, family: "body", color: "rgba(229,229,227,0.70)",
    };
    cursorBottom -= TER_H;
  }

  let secondary: SpeciesNameBox | null = null;
  if (data.nameSecondary) {
    if (tertiary) cursorBottom -= GAP_SEC_TER;
    secondary = {
      left: LEFT, bottom: H - cursorBottom, height: SEC_H,
      fontSize: 38, fontWeight: 400, family: "body", color: "rgba(255,255,255,0.80)",
    };
    cursorBottom -= SEC_H;
  }

  let primary: SpeciesNameBox | null = null;
  if (data.namePrimary) {
    if (tertiary || secondary) cursorBottom -= GAP_PRI_SEC;
    primary = {
      left: LEFT, bottom: H - cursorBottom, height: PRI_H,
      fontSize: 75, fontWeight: 500, family: "display", color: "#fff",
    };
  }

  return { tertiary, secondary, primary };
}

function drawCover(ctx: CanvasRenderingContext2D, data: CoverSlideData, style: SlideStyle, opts?: DrawOpts): void {
  const W = SLIDE_W, H = SLIDE_H;
  const DISPLAY = style.fonts.display;
  const BODY = style.fonts.body;

  ctx.clearRect(0, 0, W, H);

  const textOnLight = !data.photo && data.backgroundMode === "light";
  const rgb = textOnLight ? "28,36,32" : "255,255,255";

  if (data.photo) {
    drawCoverFitImage(ctx, data.photo, data.photoOffsetY ?? 0, data.photoZoom ?? 1);
  } else if (data.backgroundMode && data.backgroundMode in FLAT_BG_COLORS) {
    ctx.fillStyle = FLAT_BG_COLORS[data.backgroundMode as keyof typeof FLAT_BG_COLORS];
    ctx.fillRect(0, 0, W, H);
  } else if (data.backgroundMode !== "transparent") {
    // "auto" default — never ship a blank slide.
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, "#20301f");
    bg.addColorStop(1, "#0e1710");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);
  }

  // Two 450px edge bands — dark at the very top/bottom, clear through the
  // middle, so the photo stays untouched where the bird usually is.
  if (!textOnLight) drawEdgeScrims(ctx, COVER_SCRIM_H);

  ctx.textAlign = "left";

  // ── Top block: kicker, place name, region · month. A real HTML overlay
  // (FieldDiaryCoverOverlay.tsx) draws these live instead, at the exact same
  // boxes via coverTextLayout() — skip painting them twice.
  if (!opts?.skipEditableText) {
    ctx.textBaseline = "middle";
    const { kicker, headline, subtitle } = coverTextLayout(data, { display: DISPLAY, body: BODY });

    if (data.kickerText.trim()) {
      ctx.font = `${kicker.fontWeight} ${kicker.fontSize}px ${BODY}`;
      ctx.fillStyle = kicker.color;
      drawTrackedText(ctx, data.kickerText.toUpperCase(), kicker.left, kicker.middle, kicker.tracking);
    }

    ctx.font = `${headline.fontWeight} ${headline.fontSize}px ${DISPLAY}`;
    ctx.fillStyle = headline.color;
    const blockTop = headline.middle - (headline.lineCount * headline.lineHeight) / 2;
    fitSerifHeadline(
      getMeasureCtx(), data.headlineTitle, 0, headline.width, data.headlineFontSize, DISPLAY,
      { weight: 300, capTop: COVER_HEADLINE_TOP }
    ).lines.forEach((line, i) => {
      ctx.fillText(line, headline.left, blockTop + (i + 0.5) * headline.lineHeight);
    });

    if (data.subtitle.trim()) {
      ctx.font = `${subtitle.fontWeight} ${subtitle.fontSize}px ${BODY}`;
      ctx.fillStyle = subtitle.color;
      drawTrackedText(ctx, data.subtitle.toUpperCase(), subtitle.left, subtitle.middle, subtitle.tracking);
    }
  }

  // ── Bottom-left stats: emoji icon centered over its value, groups flowing
  // left to right. Cleared stats simply drop out of the row. ──
  ctx.textBaseline = "middle";
  const statScale = data.statsScale ?? 1;
  const valueSize = 44 * statScale;
  const iconSize = 34 * statScale;
  let x = COVER_PAD;
  for (const seg of data.stats) {
    const value = seg.value.trim();
    if (!value) continue;
    ctx.font = `400 ${valueSize}px ${BODY}`;
    const valueW = ctx.measureText(value).width;
    ctx.fillStyle = `rgba(${rgb},0.92)`;
    ctx.fillText(value, x, COVER_STAT_VALUE_MID);

    ctx.font = `${iconSize}px serif`;
    const iconW = ctx.measureText(seg.icon).width;
    ctx.fillText(seg.icon, x + (valueW - iconW) / 2, COVER_STAT_ICON_MID);

    x += valueW + COVER_STAT_GAP * statScale;
  }

  // ── Bottom-right swipe cue: script line with a hand-drawn arrow beneath ──
  const swipe = (data.swipeText ?? "").trim();
  if (swipe) {
    const rightX = W - COVER_PAD;
    ctx.font = `400 52px ${SCRIPT_FONT}`;
    ctx.fillStyle = `rgba(${rgb},0.92)`;
    const swipeW = measureTracked(ctx, swipe, 0);
    drawTrackedText(ctx, swipe, rightX - swipeW, COVER_STAT_ICON_MID, 0);

    ctx.strokeStyle = `rgba(${rgb},0.92)`;
    drawCurvedArrow(ctx, rightX, COVER_STAT_VALUE_MID + 6, 235);
  }

  drawWatermark(ctx, style, textOnLight);
}

/**
 * Species slide layout — pixel-exact from a Figma handoff (fixed sizes,
 * no wrapping, no shrink-to-fit; the longest known KZ/RU/EN names from
 * the Kazakhstan checklist were pre-tested to fit at these sizes).
 */
function drawSpecies(ctx: CanvasRenderingContext2D, data: SpeciesSlideData, style: SlideStyle, opts?: DrawOpts): void {
  const W = SLIDE_W, H = SLIDE_H;
  const DISPLAY = style.fonts.display;
  const BODY = style.fonts.body;

  ctx.clearRect(0, 0, W, H);

  if (data.photo) drawCoverFitImage(ctx, data.photo, data.photoOffsetY ?? 0, data.photoZoom ?? 1);

  // Bottom gradient: 1080×365, bottom:0, fading fully transparent at the top
  const GRAD_H = 365;
  const gradTop = H - GRAD_H;
  const grad = ctx.createLinearGradient(0, gradTop, 0, H);
  grad.addColorStop(0, "rgba(98,92,92,0)");
  grad.addColorStop(0.25, "rgba(74,69,69,0.22)");
  grad.addColorStop(0.5, "rgba(49,46,46,0.45)");
  grad.addColorStop(1, "rgba(0,0,0,0.70)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, gradTop, W, GRAD_H);

  // Text: left 60, bottom-anchored, never wrapped. A real HTML overlay
  // (FieldDiarySpeciesOverlay.tsx) draws these lines live instead, at the
  // exact same boxes via speciesNameLayout() — skip painting them twice.
  if (!opts?.skipEditableText) {
    const { tertiary, secondary, primary } = speciesNameLayout(data);
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";

    if (tertiary && data.nameTertiary) {
      ctx.font = `${tertiary.fontWeight} ${tertiary.fontSize}px ${BODY}`;
      ctx.fillStyle = tertiary.color;
      ctx.fillText(data.nameTertiary, tertiary.left, H - tertiary.bottom - tertiary.height / 2);
    }

    if (secondary && data.nameSecondary) {
      ctx.font = `${secondary.fontWeight} ${secondary.fontSize}px ${BODY}`;
      ctx.fillStyle = secondary.color;
      ctx.fillText(data.nameSecondary, secondary.left, H - secondary.bottom - secondary.height / 2);
    }

    if (primary && data.namePrimary) {
      ctx.font = `${primary.fontWeight} ${primary.fontSize}px ${DISPLAY}`;
      ctx.fillStyle = primary.color;
      ctx.fillText(data.namePrimary, primary.left, H - primary.bottom - primary.height / 2);
    }

    ctx.textBaseline = "alphabetic";
  }

  drawWatermark(ctx, style);
}

/** One centered, middle-anchored line's box for the closing slide — mirrors
 * speciesNameLayout's role: single source of truth shared by drawClosing
 * below and the live editor's HTML overlay (FieldDiaryClosingOverlay.tsx). */
export interface ClosingTextBox {
  top: number; fontSize: number; fontWeight: number; family: "display" | "body"; color: string;
}

export function closingTextLayout(data: ClosingSlideData): {
  heading: ClosingTextBox;
  subtext: ClosingTextBox;
  handle: ClosingTextBox | null;
} {
  const H = SLIDE_H;
  return {
    heading: { top: H / 2 - 90, fontSize: 96, fontWeight: 300, family: "display", color: "#fff" },
    subtext: { top: H / 2 + 20, fontSize: 34, fontWeight: 300, family: "body", color: "rgba(255,255,255,0.60)" },
    handle: data.handle
      ? { top: H / 2 + 160, fontSize: 34, fontWeight: 600, family: "body", color: "rgba(255,255,255,0.85)" }
      : null,
  };
}

function drawClosing(ctx: CanvasRenderingContext2D, data: ClosingSlideData, style: SlideStyle, opts?: DrawOpts): void {
  const W = SLIDE_W, H = SLIDE_H;
  const DISPLAY = style.fonts.display;
  const BODY = style.fonts.body;

  ctx.clearRect(0, 0, W, H);

  if (data.photo) drawCoverFitImage(ctx, data.photo, data.photoOffsetY ?? 0, data.photoZoom ?? 1);

  // Full soft-dark overlay so centered text reads on any photo (or plain dark bg)
  ctx.fillStyle = data.photo ? "rgba(0,0,0,0.55)" : "#111511";
  ctx.fillRect(0, 0, W, H);

  const cx = W / 2;
  const { heading, subtext, handle } = closingTextLayout(data);

  if (data.handle) {
    // thin divider — position is fixed regardless of who draws the handle text
    ctx.strokeStyle = "rgba(255,255,255,0.25)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx - 60, H / 2 + 90);
    ctx.lineTo(cx + 60, H / 2 + 90);
    ctx.stroke();
  }

  // A real HTML overlay (FieldDiaryClosingOverlay.tsx) draws these lines live
  // instead, at the exact same boxes via closingTextLayout() — skip painting
  // them twice.
  if (!opts?.skipEditableText) {
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.font = `${heading.fontWeight} ${heading.fontSize}px ${DISPLAY}`;
    ctx.fillStyle = heading.color;
    ctx.fillText(data.heading, cx, heading.top);

    ctx.font = `${subtext.fontWeight} ${subtext.fontSize}px ${BODY}`;
    ctx.fillStyle = subtext.color;
    ctx.fillText(data.subtext, cx, subtext.top);

    if (handle && data.handle) {
      ctx.font = `${handle.fontWeight} ${handle.fontSize}px ${BODY}`;
      ctx.fillStyle = handle.color;
      ctx.fillText(data.handle, cx, handle.top);
    }

    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
  }

  drawWatermark(ctx, style);
}

export const fieldDiary: CarouselTemplate = {
  id: "field-diary",
  name: "Field Diary",
  premium: false,
  drawCover,
  drawSpecies,
  drawClosing,
  useTextOverlay: { cover: true, species: true, closing: true },
};
